import { useMemo, useEffect } from 'react';
import { createTheme, ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import buildColors from './colors';
import { FONT, MuiCssBaseline } from './font';
import buildTableStyles from './table.styles';
import setColorVariables from './colorVariables';
import WBCONST from '../shared/constants/wb.const';

const {
  BUTTON_HEIGHT,
  BUTTON_RADIUS,
  HEADER_HEIGHT,
  INPUT_RADIUS,
  INPUT_HEIGHT,
  DEFAULT_THEME_COLOR,
} = WBCONST;

function container() {
  return document.fullscreenElement ?? document.body;
}

function ThemeProvider({ children }) {
  const themeColor = localStorage.getItem('themeColor') || DEFAULT_THEME_COLOR;

  const colors = useMemo(() => buildColors(themeColor), [themeColor]);
  const tableStyles = useMemo(() => buildTableStyles(colors), [colors]);

  useEffect(() => {
    setColorVariables(colors);
  }, [colors]);

  const { colorShades, text, primary, cancelButton } = colors;

  const theme = useMemo(
    () =>
      createTheme({
        palette: colors,
        spacing: 8,
        typography: {
          fontFamily: 'fontRegular, Roboto',
          caption: {
            color: colorShades.greyShade,
            fontSize: '0.857rem',
          },
          caption2: {
            color: colorShades.greyShade,
            fontFamily: FONT.w600,
            fontSize: '0.857rem',
          },
          caption3: {
            color: text.primary,
            fontFamily: FONT.w300,
            fontWeight: 275,
            fontSize: '0.857rem',
          },
          subtitle1: {
            fontFamily: FONT.w700,
            fontSize: '2.143rem',
            lineHeight: 1.5,
          },
          subtitle2: {
            fontFamily: FONT.w500,
            fontSize: '1.786rem',
          },
          subtitle3: {
            fontFamily: FONT.w400,
            fontSize: '1.714rem',
          },
          h1: {
            fontFamily: FONT.w600,
            fontSize: '2.5rem',
          },
          h2: {
            fontFamily: FONT.w600,
            fontSize: '2.143rem',
          },
          h3: {
            fontFamily: FONT.w600,
            fontSize: '1.786rem',
          },
          h4: {
            fontFamily: FONT.w600,
            fontSize: '1.429rem',
          },
          h5: {
            fontFamily: FONT.w600,
            fontSize: '1.071rem',
          },
          h6: {
            fontFamily: FONT.w600,
            fontSize: '0.714rem',
          },
        },
        components: {
          MuiCssBaseline,
          ...tableStyles,

          MuiAppBar: {
            styleOverrides: {
              root: {
                minHeight: HEADER_HEIGHT,
                border: 0,
              },
            },
          },
          MuiCircularProgress: {
            styleOverrides: {
              root: {
                color: primary.main,
              },
            },
          },

          MuiMultiSectionDigitalClockSection: {
            styleOverrides: {
              item: {
                '&.Mui-selected': {
                  backgroundColor: primary.main,
                  color: colorShades.pureWhite,
                  '&:hover': {
                    backgroundColor: primary.main,
                  },
                },
              },
            },
          },
          MuiPickersYear: {
            styleOverrides: {
              yearButton: {
                '&.Mui-selected': {
                  backgroundColor: primary.main,
                  color: colorShades.pureWhite,
                  '&:hover': {
                    backgroundColor: primary.main,
                  },
                  '&:focus': {
                    backgroundColor: primary.main,
                  },
                },
              },
            },
          },
          MuiDayCalendar: {
            styleOverrides: {
              weekDayLabel: {
                color: 'black',
                fontWeight: 'bold',
              },
            },
          },
          MuiPickersDay: {
            styleOverrides: {
              root: {
                '&.Mui-selected': {
                  backgroundColor: `${primary.main} !important`,
                  color: colorShades.white,
                  '&:hover': {
                    backgroundColor: primary.light,
                  },
                },
                '&.Mui-selected.Mui-focusVisible': {
                  backgroundColor: primary.main,
                },
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: colorShades.mediumGrey,
                  },
                  '&:hover fieldset': {
                    borderColor: primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: primary.main,
                  },
                },
              },
            },
          },

          MuiButtonBase: {
            styleOverrides: {
              root: {},
            },
          },
          MuiButton: {
            defaultProps: {
              disableElevation: true,
              variant: 'contained',
              color: 'cancelButton',
            },
            styleOverrides: {
              root: {
                fontFamily: FONT.w500,
                textTransform: 'capitalize',
                borderRadius: BUTTON_RADIUS,
                fontSize: '0.857rem',
                letterSpacing: '1px',
                fontWeight: 600,
              },
              filled: {
                color: cancelButton.contrastText,
                backgroundColor: cancelButton.main,
              },
              sizeSmall: {
                height: BUTTON_HEIGHT - 5,
              },
              sizeMedium: {
                height: BUTTON_HEIGHT,
              },
              sizeLarge: {
                height: BUTTON_HEIGHT + 5,
                fontSize: '1rem',
              },
            },
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                '&.Mui-disabled': {
                  pointerEvents: 'inherit',
                  cursor: 'not-allowed',
                },
              },
            },
          },
          MuiInputBase: {
            styleOverrides: {
              root: {
                height: INPUT_HEIGHT,
                borderColor: colorShades.lightRed,
              },
              sizeSmall: {
                height: INPUT_HEIGHT - 10,
                borderColor: colorShades.lightRed,
              },
              input: {
                '&::placeholder': {
                  fontFamily: FONT.w600,
                },
              },
            },
          },
          MuiSelect: {
            styleOverrides: {
              root: {
                background: 'inherit',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: colorShades.mediumGrey,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${primary.main} !important`,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${primary.main} !important`,
                },
              },
            },
          },
          MuiFormControl: {
            styleOverrides: {
              root: {
                marginBottom: 8,
              },
            },
          },
          MuiFormLabel: {
            styleOverrides: {
              root: {
                color: text.primary,
                fontSize: '0.9rem',
                marginBottom: '0.2rem',
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                borderRadius: INPUT_RADIUS,
                borderColor: 'red',
              },
              input: {
                padding: '11px 14px',
              },
              multiline: {
                height: 'auto',
                padding: 0,
              },
            },
          },
          MuiAutocomplete: {
            styleOverrides: {
              root: {
                '.MuiOutlinedInput-root': {
                  paddingTop: '2.5px',
                },
              },
              option: {
                color: text.primary,
              },
              noOptions: {
                color: text.sideBar,
              },
            },
          },
          MuiTabs: {
            styleOverrides: {
              root: {
                '& .MuiTab-root': {
                  color: text.sideBar,
                  borderRadius: BUTTON_RADIUS,
                },
                '& .MuiTab-root.Mui-selected': {
                  color: colorShades.pureWhite,
                  backgroundColor: primary.main,
                },
                '& .MuiTabs-indicator': {
                  display: 'none',
                },
              },
            },
          },
          MuiDialog: {
            defaultProps: {
              container,
            },
            styleOverrides: {
              root: {
                backdropFilter: 'blur(5px)',
              },
            },
          },
          MuiMenu: {
            defaultProps: {
              container,
            },
          },
          MuiCheckbox: {
            styleOverrides: {
              root: {
                color: colorShades.blackGradient2,
                '&.Mui-checked': {
                  color: primary.main,
                },
              },
            },
          },
          MuiRadio: {
            styleOverrides: {
              root: {
                color: colorShades.blackGradient2,
                '&.Mui-checked': {
                  color: primary.main,
                },
              },
            },
          },
          MuiPopper: {
            defaultProps: {
              container,
            },
            styleOverrides: {
              root: {
                '.MuiPickersYear-yearButton': {
                  fontSize: '1.143rem',
                },
              },
            },
          },
        },
      }),
    [colors, tableStyles, colorShades, text, primary, cancelButton]
  );

  return <MUIThemeProvider theme={theme}>{children}</MUIThemeProvider>;
}

export default ThemeProvider;
